// /auth/login — phone + 6-digit PIN for BOTH strategies (business app copy):
// online → Authentik business-web headless flow (uid biz:+964…, PIN on the
// password stage); node → POST /api/erp/auth/login. The screen never knows which.

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BrandMark } from '@/components/BrandMark';
import { Button, PrimaryAction } from '@/components/Button';
import { ONB, onboardingAvailable } from '@/features/onboarding';
import { Field, TextInput } from '@/components/Form';
import { AuthError, activeStrategyName, probeServerMode, signIn } from '@/lib/auth';
import { normalizeIraqiPhone } from '@/lib/digits';
import { PinInput, PIN_LENGTH } from './PinInput';
import { HOME_PATH } from './Forbidden';

export function LoginScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { from?: string; notice?: string; phone?: string } | null;
  const from = state?.from;
  // Why they are here (e.g. «انتهت الجلسة، سجّل الدخول مجدداً» from the lock screen).
  const notice = state?.notice;
  // The onboarding's «هذا الرقم مسجّل عندنا» hands the typed number over (board 25 phone--exists).
  const [phone, setPhone] = useState(state?.phone ?? '');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const pinRef = useRef<HTMLInputElement>(null);
  // Which strategy signs in is the SERVER's mode: a node says so before /me.
  const [strategy, setStrategy] = useState(activeStrategyName);
  useEffect(() => {
    let alive = true;
    void probeServerMode().then(() => {
      if (alive) setStrategy(activeStrategyName());
    });
    return () => {
      alive = false;
    };
  }, []);

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (busy) return;
    setError(null);
    if (!normalizeIraqiPhone(phone)) {
      setPhoneError('رقم الهاتف غير صحيح — يجب أن يبدأ بـ 07');
      return;
    }
    setPhoneError(null);
    if (pin.length !== PIN_LENGTH) {
      pinRef.current?.focus();
      return;
    }
    setBusy(true);
    try {
      await signIn(phone, pin);
      navigate(from && from !== '/auth/login' ? from : HOME_PATH, { replace: true });
    } catch (err) {
      setError(err instanceof AuthError ? err.message : 'تعذّر إكمال تسجيل الدخول');
      setPin('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="e-center" data-testid="login-screen" data-strategy={strategy}>
      <form className="e-card-auth" onSubmit={submit} noValidate>
        <BrandMark mode="launch" size={56} label="احجزلي" className="e-login-mark" />
        <h1 style={{ font: 'var(--t-title)' }}>تسجيل الدخول</h1>
        {notice && !error && (
          <p role="status" data-testid="login-notice" style={{ color: 'var(--slate)', font: '600 13px/1.5 var(--font-ar)' }}>
            {notice}
          </p>
        )}
        <Field label="أدخل رقم هاتفك" error={phoneError}>
          {(id) => (
            <TextInput
              id={id}
              ltr
              inputMode="tel"
              autoComplete="tel"
              placeholder="07XX XXX XXXX"
              value={phone}
              invalid={!!phoneError}
              onChange={(e) => setPhone(e.target.value)}
            />
          )}
        </Field>
        <Field label="أدخل الرمز السري">
          {() => <PinInput ref={pinRef} value={pin} onChange={setPin} label="أدخل الرمز السري" disabled={busy} invalid={!!error} />}
        </Field>
        {error && (
          <p role="alert" style={{ color: 'var(--error)', font: '600 13px/1.5 var(--font-ar)' }}>
            {error}
          </p>
        )}
        <PrimaryAction type="submit" size="lg" block loading={busy}>
          {busy ? 'جارٍ التحقق…' : 'دخول'}
        </PrimaryAction>
        {/* Online only: a node starts from its own first run (/setup). Secondary, never coral. */}
        {/* (re-evaluated when the mode probe above re-renders the screen) */}
        {onboardingAvailable() && (
          <Button variant="ghost" block onClick={() => navigate(ONB.role)} data-testid="login-onboarding">
            إنشاء نشاط جديد
          </Button>
        )}
      </form>
    </div>
  );
}
